/**
 * The tour in progress, a page tour (SA-1315, spec §6) or a task tour (SA-1316). One tour at a time; it survives route
 * changes inside the app (a step may be on the next page) and ends on reload (memory only).
 */
import { create } from 'zustand'
import { recordTour } from '@/services/feedbackContext/capture'
import type { AnyTour } from '@/services/tour/tourModel'

export type TourOrigin = 'panel' | 'menu'
/** `finding`: looking for the step's target; `shown`: ringed; `missing`: its words with Skip. */
export type TourStatus = 'idle' | 'finding' | 'shown' | 'missing'

interface TourState {
  tour: AnyTour | null
  step: number
  origin: TourOrigin | null
  status: TourStatus
  start: (tour: AnyTour, origin: TourOrigin) => void
  next: () => void
  back: () => void
  /** Leave the tour before its end: ✕, Escape, End tour, Exit. */
  exit: () => void
  setStatus: (status: Exclude<TourStatus, 'idle'>) => void
}

const IDLE = { tour: null, step: 0, origin: null, status: 'idle' as TourStatus }

export const useTourStore = create<TourState>()((set, get) => ({
  ...IDLE,
  start: (tour, origin) => {
    set({ tour, step: 0, origin, status: 'finding' })
    recordTour('start', tour.id, 0)
  },
  next: () => {
    const { tour, step } = get()
    if (!tour) return
    if (step >= tour.steps.length - 1) {
      set(IDLE)
      recordTour('done', tour.id, step)
      return
    }
    set({ step: step + 1, status: 'finding' })
    recordTour('step', tour.id, step + 1)
  },
  back: () => {
    const { tour, step } = get()
    if (!tour || step === 0) return
    set({ step: step - 1, status: 'finding' })
    recordTour('step', tour.id, step - 1)
  },
  exit: () => {
    const { tour, step } = get()
    if (!tour) return
    set(IDLE)
    recordTour('exit', tour.id, step)
  },
  setStatus: (status) => {
    const { tour, step, status: was } = get()
    if (!tour || was === status) return
    set({ status })
    if (status === 'missing') recordTour('missing', tour.id, step)
  },
}))
